import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Building2,
  Target,
  Sparkles,
  GraduationCap,
  ListChecks,
  Image as ImageIcon,
  Video,
} from 'lucide-react'
import {
  courseService,
  Course,
  formatCourseType,
  formatDifficulty,
  formatPrice,
  formatDuration,
  getCourseSchool,
} from '../services/courseService'
import ConfirmationDialog from '../components/ConfirmationDialog'
import CourseScheduleDates from '../components/CourseScheduleDates'
import { hasCourseScheduleSection } from '../utils/courseSchedule'
import toast from 'react-hot-toast'

const CourseDetail = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const [course, setCourse] = useState<Course | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [showToggleConfirm, setShowToggleConfirm] = useState(false)

  useEffect(() => {
    if (courseId) {
      fetchCourseDetails()
    }
  }, [courseId])

  const fetchCourseDetails = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await courseService.getCourseById(courseId!)
      const courseData = response?.data?.course ?? response?.course ?? response
      if (response?.success && courseData) {
        setCourse(courseData)
      } else {
        setError('Course not found')
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load course details')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleStatusClick = () => {
    setShowToggleConfirm(true)
  }

  const handleToggleStatusConfirm = async () => {
    if (!course) return
    try {
      setActionLoading(true)
      const response = await courseService.toggleCourseStatus(course._id, !course.isActive)
      if (response?.success) {
        setShowToggleConfirm(false)
        toast.success(course.isActive ? 'Course deactivated' : 'Course activated')
        // Refetch to ensure UI reflects the updated state
        const refreshed = await courseService.getCourseById(courseId!)
        const updated = refreshed?.data?.course ?? refreshed?.course ?? refreshed
        if (updated) setCourse(updated)
        else setCourse(prev => prev ? { ...prev, isActive: !prev.isActive } : prev)
      } else {
        setCourse(prev => prev ? { ...prev, isActive: !prev.isActive } : prev)
        setShowToggleConfirm(false)
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || 'Failed to update status'
      toast.error(msg)
      setShowToggleConfirm(false)
    } finally {
      setActionLoading(false)
    }
  }

  const formatDate = (dateStr?: string | Date) => {
    if (!dateStr) return null
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return String(dateStr)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500" />
      </div>
    )
  }

  if (error || !course) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-gray-600 mb-4">{error || 'Course not found'}</p>
        <button
          onClick={() => navigate('/admin/courses')}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
        >
          Back to Courses
        </button>
      </div>
    )
  }

  const school = getCourseSchool(course)

  return (
    <div className="space-y-6">
      {/* Header with back and status */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/courses')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{course.name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {school?.name} {school?.city && `• ${school.city}`} {school?.country && `• ${school.country}`}
              {(school?.approvalStatus || school?.isActive === false) && (
                <span className="block mt-1 text-xs text-amber-800">
                  School: {school?.approvalStatus && <span>{school.approvalStatus} approval</span>}
                  {school?.approvalStatus && school?.isActive === false ? ' • ' : ''}
                  {school?.isActive === false && <span>Inactive listing</span>}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${
              course.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {course.isActive ? <CheckCircle className="h-4 w-4 mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
            {course.isActive ? 'Active' : 'Inactive'}
          </span>
          {course.isFeatured && (
            <span className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-yellow-100 text-yellow-800">
              <Star className="h-4 w-4 mr-2 fill-current" />
              Featured
            </span>
          )}
          <button
            onClick={handleToggleStatusClick}
            disabled={actionLoading}
            className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
              course.isActive
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {actionLoading ? (
              <span className="animate-spin mr-2">⏳</span>
            ) : course.isActive ? (
              <XCircle className="h-4 w-4 mr-2" />
            ) : (
              <CheckCircle className="h-4 w-4 mr-2" />
            )}
            {course.isActive ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      {/* Activate/Deactivate confirmation */}
      <ConfirmationDialog
        isOpen={showToggleConfirm}
        onClose={() => setShowToggleConfirm(false)}
        onConfirm={handleToggleStatusConfirm}
        title={course.isActive ? 'Deactivate Course?' : 'Activate Course?'}
        message={course.isActive
          ? 'This course will be hidden from the public listing. Students will not be able to book it.'
          : 'This course will be visible on the public listing. Students will be able to book it.'}
        confirmText={course.isActive ? 'Deactivate' : 'Activate'}
        cancelText="Cancel"
        type={course.isActive ? 'warning' : 'success'}
        isLoading={actionLoading}
      />

      {/* Main content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-8 space-y-8">
          {/* Overview */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BookOpen className="h-5 w-5 mr-2 text-amber-500" />
              Overview
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <span className="text-sm text-gray-500 block mb-1">Type</span>
                <span className="font-medium">{formatCourseType(course.type || 'COURSE')}</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <span className="text-sm text-gray-500 block mb-1">Difficulty</span>
                <span className="font-medium">{formatDifficulty(course.difficulty || 'ALL_LEVELS')}</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <span className="text-sm text-gray-500 block mb-1">Duration</span>
                <span className="font-medium">{formatDuration(course.duration ?? 0)}</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <span className="text-sm text-gray-500 block mb-1">
                  Price {course.priceRange ? '(from rooms)' : ''}
                </span>
                <span className="font-medium">
                  {course.priceRange
                    ? `${formatPrice(course.priceRange.min, course.priceRange.currency)} – ${formatPrice(course.priceRange.max, course.priceRange.currency)}`
                    : formatPrice(course.price ?? 0, course.currency || 'USD')}
                </span>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <span className="text-sm text-gray-500 block mb-1">Max Students</span>
                <span className="font-medium">{course.maxStudents ?? '-'}</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <span className="text-sm text-gray-500 block mb-1">Rating</span>
                <span className="font-medium">
                  {course.rating ?? 0}/5 ({course.totalReviews ?? 0} reviews)
                </span>
              </div>
              {course.createdBySource && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <span className="text-sm text-gray-500 block mb-1">Created by</span>
                  <span className="font-medium">
                    {course.createdBySource === 'BOOKMYYOGATTC' ? 'Bookmyyogattc (admin)' : 'School (self-serve)'}
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* Description */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BookOpen className="h-5 w-5 mr-2 text-amber-500" />
              Description
            </h2>
            <div className="prose max-w-none text-gray-600">
              <p className="whitespace-pre-wrap">{course.description || 'No description'}</p>
              {course.shortDescription && (
                <p className="mt-4 text-sm text-gray-500">{course.shortDescription}</p>
              )}
            </div>
          </section>

          {/* Schedule */}
          {hasCourseScheduleSection(course.schedule) && (
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-amber-500" />
                Schedule
              </h2>
              <div className="space-y-4">
                {course.schedule?.startDate && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Fixed start date</span>
                    <span>{formatDate(course.schedule.startDate)}</span>
                  </div>
                )}
                {course.schedule?.endDate && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Fixed end date</span>
                    <span>{formatDate(course.schedule.endDate)}</span>
                  </div>
                )}
                <CourseScheduleDates schedule={course.schedule} />
                {course.schedule?.timeSlots?.length ? (
                  <div className="mt-4">
                    <span className="text-sm font-medium text-gray-600 block mb-2">Time Slots</span>
                    <div className="space-y-2">
                      {course.schedule.timeSlots.map((slot, i) => (
                        <div key={i} className="flex justify-between text-sm py-1">
                          <span>{slot.day}</span>
                          <span>{slot.startTime} - {slot.endTime}{slot.label ? ` (${slot.label})` : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </section>
          )}

          {/* Accommodation — show all room types linked to the course (admin API includes inactive rooms) */}
          {!!course.accommodation?.rooms?.length && (
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Building2 className="h-5 w-5 mr-2 text-amber-500" />
                Accommodation & room types
              </h2>
              <p className="text-sm text-gray-600 mb-3">
                {course.accommodation.available
                  ? 'Accommodation is offered on this course.'
                  : 'Accommodation marked unavailable, but configured room pricing is listed for admin reference.'}
              </p>
              <div className="space-y-4">
                {course.accommodation.rooms?.map((room, i) => (
                  <div
                    key={room.roomId || `room-${i}`}
                    className={`rounded-lg p-4 border ${
                      room.roomIsActive === false
                        ? 'bg-red-50/80 border-red-200'
                        : 'bg-gray-50 border-gray-100'
                    }`}
                  >
                    <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {room.name?.trim()
                              ? `${room.name.trim()} (${room.type || 'Room'})`
                              : room.type || 'Room'}
                          </span>
                          {room.roomIsActive === false && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800">
                              Inactive room
                            </span>
                          )}
                        </div>
                        {room.roomId && (
                          <p className="text-xs text-gray-500 mt-1 font-mono">Room ID: {room.roomId}</p>
                        )}
                      </div>
                      <span className="font-semibold text-gray-900">
                        {formatPrice(room.price ?? 0, course.currency || 'USD')}
                        {room.bookingAmount != null && room.bookingAmount > 0 && (
                          <span className="block text-xs font-normal text-gray-500 mt-0.5">
                            Booking amount: {formatPrice(room.bookingAmount, course.currency || 'USD')}
                          </span>
                        )}
                      </span>
                    </div>
                    {(room.capacity != null || room.amenities?.length) && (
                      <div className="text-sm text-gray-600">
                        {room.capacity != null && `Capacity: ${room.capacity}`}
                        {room.capacity != null && room.amenities?.length ? ' • ' : ''}
                        {room.amenities?.length ? room.amenities.join(', ') : ''}
                      </div>
                    )}
                    {room.description && <p className="text-xs text-gray-600 mt-2">{room.description}</p>}
                  </div>
                ))}
                {course.accommodation.mealPlan && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <span className="font-medium text-green-800">Meal Plan: {course.accommodation.mealPlan.type}</span>
                    {course.accommodation.mealPlan.description && (
                      <p className="text-sm text-green-700 mt-2">{course.accommodation.mealPlan.description}</p>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Lists: Inclusions, Requirements, Who is this for, etc. */}
          {(course.inclusions?.length || course.requirements?.length || course.whoIsThisFor?.length || course.courseHighlights?.length || course.whatYouWillGain?.length) && (
            <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {course.inclusions?.length ? (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                    Inclusions
                  </h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {course.inclusions.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {course.requirements?.length ? (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <ListChecks className="h-4 w-4 mr-2 text-blue-500" />
                    Requirements
                  </h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {course.requirements.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {course.whoIsThisFor?.length ? (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <Target className="h-4 w-4 mr-2 text-purple-500" />
                    Who is this for
                  </h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {course.whoIsThisFor.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {course.courseHighlights?.length ? (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <Sparkles className="h-4 w-4 mr-2 text-amber-500" />
                    Course Highlights
                  </h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {course.courseHighlights.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {course.whatYouWillGain?.length ? (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <GraduationCap className="h-4 w-4 mr-2 text-indigo-500" />
                    What you will gain
                  </h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {course.whatYouWillGain.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </section>
          )}

          {/* Curriculum */}
          {course.curriculum?.length ? (
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <BookOpen className="h-5 w-5 mr-2 text-amber-500" />
                Curriculum
              </h2>
              <div className="space-y-4">
                {course.curriculum.map((section, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">{section.title}</h4>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      {section.items?.map((item, j) => (
                        <li key={j}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {/* FAQ */}
          {course.faq?.length ? (
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-amber-500" />
                Frequently asked questions
              </h2>
              <div className="space-y-4">
                {course.faq.map((item, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-4">
                    <p className="font-medium text-gray-900">{item.question}</p>
                    <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {/* Timetable */}
          {course.timetable?.length ? (
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-amber-500" />
                Daily Timetable
              </h2>
              <ul className="list-decimal list-inside text-gray-600 space-y-2">
                {course.timetable.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* Media */}
          {((course.media?.images?.length || course.images?.length) || course.media?.videos?.length) ? (
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ImageIcon className="h-5 w-5 mr-2 text-amber-500" />
                Media
              </h2>
              <div className="space-y-4">
                {(course.media?.images?.length || course.images?.length) ? (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <ImageIcon className="h-4 w-4 mr-1" /> Images
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[...(course.media?.images || []), ...(course.images || [])]
                        .filter((s): s is string => !!s)
                        .slice(0, 8)
                        .map((src, i) => (
                          <img
                            key={i}
                            src={src.startsWith('http') ? src : (import.meta.env.VITE_MEDIA_BASE_URL ? `${import.meta.env.VITE_MEDIA_BASE_URL}/${src}` : src)}
                            alt={`Course ${i + 1}`}
                            className="w-full h-32 object-cover rounded-lg bg-gray-100"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                          />
                        ))}
                    </div>
                  </div>
                ) : null}
                {course.media?.videos?.length ? (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Video className="h-4 w-4 mr-1" /> Videos
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {course.media.videos
                        .filter((s): s is string => !!s)
                        .slice(0, 4)
                        .map((src, i) => (
                          <video
                            key={i}
                            src={src.startsWith('http') ? src : (import.meta.env.VITE_MEDIA_BASE_URL ? `${import.meta.env.VITE_MEDIA_BASE_URL}/${src}` : undefined)}
                            controls
                            className="w-full rounded-lg bg-gray-100"
                          />
                        ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {/* Meta */}
          <section className="border-t pt-6">
            <div className="flex flex-wrap gap-6 text-sm text-gray-500">
              <span>Created: {formatDate(course.createdAt)}</span>
              <span>Updated: {formatDate(course.updatedAt)}</span>
              <span>Total Bookings: {course.totalBookings ?? 0}</span>
              {course.yogaAllianceCertified && (
                <span className="text-green-600 font-medium">Yoga Alliance Certified</span>
              )}
            </div>
          </section>
        </div>
      </motion.div>
    </div>
  )
}

export default CourseDetail
